import { useState } from "react";

const Ads = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const PIPEDRIVE_API_TOKEN = import.meta.env.VITE_PIPEDRIVE_API_TOKEN;
      const PIPEDRIVE_DOMAIN = import.meta.env.VITE_PIPEDRIVE_DOMAIN || "api.pipedrive.com";

      // 1. Create Person in Pipedrive
      const personBody = {
        name: formData.name,
        email: [{ value: formData.email, primary: true }],
      };

      // Only add phone if provided
      if (formData.phone) {
        personBody.phone = [{ value: formData.phone, primary: true }];
      }

      // Only add custom source field if configured in Pipedrive
      if (import.meta.env.VITE_PIPEDRIVE_SOURCE_FIELD) {
        personBody[import.meta.env.VITE_PIPEDRIVE_SOURCE_FIELD] = "Google Ads";
      }

      const personResponse = await fetch(
        `https://${PIPEDRIVE_DOMAIN}/v1/persons?api_token=${PIPEDRIVE_API_TOKEN}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(personBody),
        }
      );

      if (!personResponse.ok) {
        throw new Error(`Person creation failed: ${personResponse.status}`);
      }

      const personData = await personResponse.json();
      const personId = personData.data.id;

      // 2. Create Organization if company provided
      let orgId = null;
      if (formData.company) {
        const orgResponse = await fetch(
          `https://${PIPEDRIVE_DOMAIN}/v1/organizations?api_token=${PIPEDRIVE_API_TOKEN}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              name: formData.company,
            }),
          }
        );

        if (orgResponse.ok) {
          const orgData = await orgResponse.json();
          orgId = orgData.data.id;

          // Update person with organization
          await fetch(
            `https://${PIPEDRIVE_DOMAIN}/v1/persons/${personId}?api_token=${PIPEDRIVE_API_TOKEN}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                org_id: orgId,
              }),
            }
          );
        }
      }

      // 3. Create Deal
      const dealResponse = await fetch(
        `https://${PIPEDRIVE_DOMAIN}/v1/deals?api_token=${PIPEDRIVE_API_TOKEN}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            title: `Google Ads Lead - ${formData.name}`,
            person_id: personId,
            org_id: orgId,
            value: 499,
            currency: "EUR",
            channel: "Web forms",
          }),
        }
      );

      if (!dealResponse.ok) {
        throw new Error(`Deal creation failed: ${dealResponse.status}`);
      }

      const dealData = await dealResponse.json();
      const dealId = dealData.data.id;

      // 4. Create Follow-up Activity
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dueDate = tomorrow.toISOString().split("T")[0];

      await fetch(
        `https://${PIPEDRIVE_DOMAIN}/v1/activities?api_token=${PIPEDRIVE_API_TOKEN}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            subject: `Follow-up: Google Ads Lead von ${formData.name}`,
            type: "call",
            due_date: dueDate,
            due_time: "10:00",
            duration: "00:30",
            deal_id: dealId,
            person_id: personId,
            org_id: orgId,
            note: `Google Ads Lead. Kontakt: ${formData.email}${formData.phone ? ` / ${formData.phone}` : ""}`,
          }),
        }
      );

      setSuccess(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
      });
    } catch (err) {
      console.error("Ads submission error:", err);
      setError(
        "Es gab ein Problem beim Absenden. Bitte versuche es erneut oder kontaktiere uns direkt."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-12 md:py-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Value Proposition */}
            <div className="space-y-6">
              <div className="inline-block">
                <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  🚀 Deutschlands führende Jobdatenbank
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Finde die perfekten{" "}
                <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                  Kandidaten
                </span>{" "}
                in Sekunden
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                Über 1 Million aktuelle Jobanzeigen, intelligente KI-Suche und direkte LinkedIn-Integration. 
                Spare bis zu 70% Zeit bei der Kandidatensuche.
              </p>

              {/* Benefits */}
              <div className="space-y-4 pt-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">1+ Million Jobanzeigen</h3>
                    <p className="text-gray-600">Täglich aktualisiert aus allen wichtigen Jobportalen</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">KI-gestützte Suche</h3>
                    <p className="text-gray-600">Finde passende Kandidaten mit intelligenten Filtern</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">LinkedIn & XING Integration</h3>
                    <p className="text-gray-600">Direkte Social Links zu HR-Managern und Recruitern</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Export & API</h3>
                    <p className="text-gray-600">Exportiere Daten oder integriere per API</p>
                  </div>
                </div>
              </div>

              {/* Trust Indicators */}
              <div className="pt-6 border-t border-gray-200">
                <div className="flex items-center gap-6 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">⭐</span>
                    <span className="font-semibold">4.9/5 Bewertung</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">✓</span>
                    <span className="font-semibold">500+ Kunden</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🔒</span>
                    <span className="font-semibold">DSGVO-konform</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Jetzt kostenlos testen
                </h2>
                <p className="text-gray-600">
                  Starte noch heute und finde deine perfekten Kandidaten
                </p>
              </div>

              {success ? (
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 text-center">
                  <div className="text-5xl mb-4">🎉</div>
                  <h3 className="text-xl font-bold text-green-800 mb-2">
                    Vielen Dank!
                  </h3>
                  <p className="text-green-700">
                    Wir melden uns innerhalb von 24 Stunden bei dir, um deine Demo einzurichten.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name */}
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-colors outline-none"
                      placeholder="Max Mustermann"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      E-Mail <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-colors outline-none"
                      placeholder="max@firma.de"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Telefon (optional)
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-colors outline-none"
                      placeholder="+49 123 456789"
                    />
                  </div>

                  {/* Company */}
                  <div>
                    <label
                      htmlFor="company"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Firma (optional)
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-colors outline-none"
                      placeholder="Deine Firma GmbH"
                    />
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold py-4 px-8 rounded-lg transform transition-all duration-300 hover:scale-105 hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 text-lg"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg
                          className="animate-spin h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Wird gesendet...
                      </span>
                    ) : (
                      "Jetzt kostenlos Demo anfordern"
                    )}
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    Mit dem Absenden stimmst du unseren{" "}
                    <a href="/datenschutz" className="text-purple-600 hover:underline">
                      Datenschutzbestimmungen
                    </a>{" "}
                    zu.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-50 border-t border-gray-200 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-gray-600 text-sm">
              © 2025 Jobdaten. Alle Rechte vorbehalten. |{" "}
              <a href="/impressum" className="text-purple-600 hover:underline">
                Impressum
              </a>{" "}
              |{" "}
              <a href="/datenschutz" className="text-purple-600 hover:underline">
                Datenschutz
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ads;
